/**
 * Tente de verrouiller l’écran en paysage (Screen Orientation API).
 * Le support varie : Chrome Android / PWA se comporte le mieux ; Safari iOS est très limité.
 * Certains navigateurs n’acceptent lock() qu’après un geste utilisateur — on retente au premier tap.
 */
import { useEffect } from "react";

/** Sous-ensemble utile de l’API (les lib DOM ne typent pas toujours lock/unlock). */
interface ScreenOrientationWithLock extends ScreenOrientation {
  lock(orientation: OrientationLockType): Promise<void>;
  unlock(): void;
}

type OrientationLockType =
  | "any"
  | "natural"
  | "landscape"
  | "portrait"
  | "portrait-primary"
  | "portrait-secondary"
  | "landscape-primary"
  | "landscape-secondary";

const tryLockLandscape = async (): Promise<boolean> => {
  const o = screen.orientation as ScreenOrientationWithLock | undefined;
  if (!o || typeof o.lock !== "function") {
    return false;
  }
  try {
    await o.lock("landscape-primary");
    return true;
  } catch {
    return false;
  }
};

export const useLandscapeLock = (enabled: boolean = true) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      await tryLockLandscape();
    };

    void run();

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void run();
      }
    };

    const onFirstInteraction = () => {
      void run();
      document.removeEventListener("pointerdown", onFirstInteraction);
    };

    document.addEventListener("visibilitychange", onVisible);
    document.addEventListener("pointerdown", onFirstInteraction, {
      passive: true,
    });

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      document.removeEventListener("pointerdown", onFirstInteraction);
      try {
        (screen.orientation as ScreenOrientationWithLock | undefined)?.unlock?.();
      } catch {
        /* ignore */
      }
    };
  }, [enabled]);
};
